import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Download, FileText, Table2, Target } from 'lucide-react';
import { getExportUrl } from '../../api/analytics';

export function ExportTab({ range }) {
  const handleExport = (type) => {
    const url = getExportUrl(type, range);
    window.open(url, '_blank');
  };

  const exportOptions = [
    {
      type: 'overview',
      title: 'Overview Data',
      description: 'Export all overview metrics including views, clicks, orders, and revenue',
      icon: <FileText className="h-6 w-6" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      type: 'listings',
      title: 'Listings Performance',
      description: 'Export detailed performance data for all your listings',
      icon: <Table2 className="h-6 w-6" />,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      type: 'ads',
      title: 'Sponsored Listings Data',
      description: 'Export impression share, coverage hours, and ad campaign metrics',
      icon: <Target className="h-6 w-6" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle>Export Analytics Data</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">
            Download your analytics data as CSV files for further analysis in spreadsheet software.
            All exports respect your current time range selection: <strong>{range}</strong>.
          </p>
        </CardContent>
      </Card>

      {/* Export Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {exportOptions.map((option) => (
          <Card key={option.type} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Icon */}
                <div className={`p-3 ${option.bgColor} rounded-lg inline-block`}>
                  <div className={option.color}>
                    {option.icon}
                  </div>
                </div>

                {/* Title & Description */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{option.title}</h3>
                  <p className="text-sm text-gray-600">{option.description}</p>
                </div>

                {/* Export Button */}
                <Button
                  onClick={() => handleExport(option.type)}
                  className="w-full gap-2"
                  variant="outline"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Export Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-blue-900">CSV Export Information</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li className="flex items-start gap-2">
                <span className="font-semibold">•</span>
                <span>CSV files can be opened in Excel, Google Sheets, or any spreadsheet software</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold">•</span>
                <span>Files include all available data for the selected time range</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold">•</span>
                <span>Maximum 10,000 rows per export to ensure file quality</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold">•</span>
                <span>Currency values are in cents (divide by 100 for dollar amounts)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold">•</span>
                <span>Rates and percentages are in decimal format (0.15 = 15%)</span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Data Retention Notice */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <p className="text-xs text-gray-600">
            <strong>Data Retention:</strong> Analytics data is retained for 90 days. 
            We recommend regular exports for long-term record keeping and analysis.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
